/**
 * Performance Container - Main Entry Point
 *
 * Dynamically loads Chat and/or Visual packages based on environment mode
 * Modes: chat-only, visual-blocks, webrtc-video-audio, chat-visual-blocks, chat_webrtc-video-audio
 *
 * Architecture:
 * - Multi-Signal Status System: Clock (TD heartbeat) + TD Ready (WebRTC backend) + WebRTC (browser connection)
 * - Status Indicator: Unified button/circle showing online/offline/WebRTC states
 * - Notification System: 3-second "connection lost" → final state transition
 *
 * Signal Priority:
 * 1. WebRTC active → webrtc.js controls button, main.js controls circle
 * 2. No WebRTC → Clock OR TD Ready determines online/offline (button + circle)
 * 3. No signals → offline state
 */

import { io } from 'socket.io-client';
import './performance.css';
import {
  createStatusButton,
  initStatusFromBackend,
  showNotification,
  showWebRTCState,
  updateButtonOnlineStatus,
  registerWebRTCClickHandler
} from '../../status-indicator/src/status-indicator.js';
import '../../status-indicator/src/status-indicator.css';

/* ------------------------------------------------------------
   Global API Exports
   Make functions available for loaded packages (chat.js, visual.js, webrtc.js)
------------------------------------------------------------ */
window.io = io;
window.createStatusButton = createStatusButton;
window.showWebRTCState = showWebRTCState;
window.updateButtonOnlineStatus = updateButtonOnlineStatus;
window.registerWebRTCClickHandler = registerWebRTCClickHandler;

/**
 * Notification Wrapper with 3-Second Timeout Logic
 *
 * When "disconnected" notification is shown:
 * 1. Display "connection lost" for 3 seconds
 * 2. After timeout, check signals and show appropriate final state
 * 3. WebRTC → play button, Clock/TD Ready → "maschin online", No signals → "maschin offline"
 */
const originalShowNotification = showNotification;
window.showNotification = function(type, customText = null) {
  originalShowNotification(type, customText);

  if (type === 'disconnected') {
    setTimeout(() => {
      console.log('[🔘 Status] "connection lost" timeout (3s) - showing final state');

      if (signals.webrtc) {
        console.log('  → WebRTC still active - showing play button');
        if (window.updateWebRTCButtonState) {
          window.updateWebRTCButtonState();
        }
      } else if (signals.clock || signals.tdReady) {
        console.log('  → Clock or TD Ready active - showing "maschin online"');
        updateButtonOnlineStatus(true);
      } else {
        console.log('  → No signals - showing "maschin offline"');
        updateButtonOnlineStatus(false);
      }
    }, 3000);
  }
};


const mode = import.meta.env.MODE || 'chat-visual-blocks';
console.log(`🚀 Performance: Loading mode '${mode}'`);

/* ------------------------------------------------------------
   Multi-Signal Status System
   Tracks connection state from multiple independent sources
------------------------------------------------------------ */

// Status UI element
const connectionStatus = document.getElementById("connection-status");

// Timeout tracking
let connectionTimeout = null;
let disconnectTimeout = null;
let clockTimeout = null;
const DISCONNECT_DELAY_MS = 5000; // 5s delay before showing disconnected (allows signals to arrive)

/**
 * Signal State (Single Source of Truth)
 *
 * Three independent signals determine online/offline status:
 * - clock: TD heartbeat (3s timeout if missing)
 * - tdReady: TD WebRTC backend connected to Status backend
 * - webrtc: Browser WebRTC peer connection to TD (managed by webrtc.js)
 *
 * Online if ANY signal is true (Clock OR TD Ready OR WebRTC)
 */
const signals = {
  clock: false,      // Clock heartbeat from TouchDesigner (via Status backend)
  webrtc: false,     // WebRTC connection state browser→TD (via webrtc.js)
  tdReady: false     // TouchDesigner WebRTC backend ready (via Status backend)
};

window.signals = signals; // Make accessible for webrtc.js

// Reconnect detection
let wasDisconnected = true;
let reconnectCooldown = false;
const RECONNECT_COOLDOWN_MS = 6000; // 6s cooldown after disconnect prevents false reconnects

/* ------------------------------------------------------------
   Status Update Logic
   Updates button/circle based on current signal state
------------------------------------------------------------ */

/**
 * Update Button and Circle Based on Signals
 *
 * Rules:
 * 1. WebRTC active → webrtc.js controls button (play/pause), main.js controls circle (green)
 * 2. WebRTC inactive → Clock OR TD Ready determines online/offline (controls both button and circle)
 * 3. No signals → offline (gray circle, "maschin offline" button)
 */
function updateOverallStatus() {
  console.log('[🔌 Status] Update');
  console.log(`  → Clock: ${signals.clock}`);
  console.log(`  → TD Ready: ${signals.tdReady}`);
  console.log(`  → WebRTC: ${signals.webrtc}`);

  // WebRTC active: Circle green, button managed by webrtc.js
  if (signals.webrtc) {
    console.log('[🔌 Status] WebRTC active - updating circle only (webrtc.js controls button)');
    updateCircleOnly(true);
    if (window.updateOverlayStatus) window.updateOverlayStatus(true);
    return;
  }

  // WebRTC inactive: Clock OR TD Ready determines button + circle
  const isOnline = signals.clock || signals.tdReady;
  console.log(`[🔌 Status] No WebRTC - Clock: ${signals.clock}, TD Ready: ${signals.tdReady} → ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
  updateButtonOnlineStatus(isOnline);
  if (window.updateOverlayStatus) window.updateOverlayStatus(isOnline);
}

// Expose globally for webrtc.js to trigger updates
window.updateOverallStatus = updateOverallStatus;

/* ------------------------------------------------------------
   Socket.IO Signal Listeners
   Receives status updates from Status backend (port 3003)
------------------------------------------------------------ */

/**
 * Connect to Status Backend and Listen for Signals
 *
 * Three signal types:
 * 1. clockHeartbeat - TD sends heartbeat every frame, 3s timeout if missing
 * 2. webrtcStatus - WebRTC connection state changes (connected/disconnected)
 * 3. tdReadyStatus - TD WebRTC backend connection state (ready/not ready)
 */
function connectStatusSocket() {
  const statusSocket = io({ path: '/socket.io/status/' });

  statusSocket.on('connect', () => {
    console.log('[🔌 Status] Socket Connected');
  });

  statusSocket.on('disconnect', () => {
    console.log('[🔌 Status] Socket Disconnected');
  });

  /**
   * Clock Heartbeat Signal
   * TD sends this every frame via POST /status/clock
   * Backend broadcasts to browsers, starts 3s timeout
   */
  statusSocket.on('clockHeartbeat', () => {
    console.log('[🔌 Status] Clock heartbeat received');

    signals.clock = true;
    clearTimeout(clockTimeout);

    // Start 3s timeout - if no heartbeat received, set clock to false
    clockTimeout = setTimeout(() => {
      console.log('[🔌 Status] Clock timeout (3s)');
      signals.clock = false;

      // Show "connection lost" ONLY if going fully offline (no other signals)
      const stillOnline = signals.webrtc || signals.tdReady;
      if (!stillOnline && window.showNotification) {
        console.log('[🔌 Status] Going fully offline - showing "connection lost"');
        window.showNotification('disconnected');
      } else if (stillOnline) {
        console.log('[🔌 Status] WebRTC or TD Ready still active - no "connection lost" notification');
      }

      updateOverallStatus();
    }, 3000);

    updateOverallStatus();
  });

  /**
   * WebRTC Connection Signal
   * Browser sends this via webrtc.js when peer connection state changes
   * NOTE: webrtc.js also sets signals.webrtc locally for instant feedback
   */
  statusSocket.on('webrtcStatus', (data) => {
    console.log('[🔌 Status] WebRTC signal received');
    console.log(`  → Type: ${data.type}`);

    const newWebRTCState = (data.type === 'connected');

    // Only update if state changed (webrtc.js already sets local signal)
    if (signals.webrtc !== newWebRTCState) {
      console.log(`  → WebRTC state changed: ${signals.webrtc} → ${newWebRTCState}`);
      signals.webrtc = newWebRTCState;
      updateOverallStatus();
    } else {
      console.log(`  → WebRTC state unchanged (${newWebRTCState}), skipping update`);
    }
  });

  /**
   * TouchDesigner Ready Signal
   * WebRTC backend notifies Status backend when TD connects/disconnects
   * Status backend broadcasts this to all browsers
   */
  statusSocket.on('tdReadyStatus', (data) => {
    console.log('[🔌 Status] TD Ready signal received');
    console.log(`  → Ready: ${data.ready}`);

    signals.tdReady = data.ready;

    // Only update UI if TD becomes ready (true)
    // If TD disconnects (false), webrtc.js already shows "disconnected" notification
    // After 3s timeout, wrapper will check signals and show final state
    if (data.ready) {
      console.log('[🔌 Status] TD became ready - updating status');
      updateOverallStatus();
    } else {
      console.log('[🔌 Status] TD disconnected - signal updated, waiting for notification timeout');
    }
  });

  console.log('[🔌 Status] Socket listeners initialized (Clock + WebRTC + TD Ready)');
}

/* ------------------------------------------------------------
   Legacy API Functions
   Provided for compatibility with Chat/Visual packages
------------------------------------------------------------ */

/**
 * Simple Status Update (Legacy)
 * Used by visual packages to show/hide connection status
 * NOTE: Prefer using signals + updateOverallStatus() instead
 */
window.updateStatus = function(message, hideStatus = false) {
  if (!connectionStatus) return;

  if (hideStatus) {
    connectionStatus.classList.add("hidden");
    connectionStatus.classList.remove("connected", "disconnected");
  } else {
    connectionStatus.textContent = message;
    connectionStatus.classList.remove("hidden");
  }
};

/**
 * Connection Status Update (Legacy + Reconnect Detection)
 *
 * Originally managed circle/button directly, now handles:
 * 1. Reconnect detection (triggers Node-RED push)
 * 2. Body class updates (is-connected/is-disconnected for CSS)
 * 3. Cooldown after disconnect (prevents false reconnects)
 *
 * NOTE: Circle/button status is now managed by updateOverallStatus() based on signals
 */
window.updateConnectionStatus = function(connected, instantOffline = false) {
  if (!connectionStatus) return;

  console.log(`[🔌 Status] Update: ${connected ? 'ONLINE' : 'OFFLINE'}${instantOffline ? ' (instant)' : ''}`);

  if (connected) {
    // Cooldown check: Prevent reconnect triggers during cooldown (except WebRTC)
    const isWebRTCUpdate = signals.webrtc;
    if (reconnectCooldown && !isWebRTCUpdate) {
      console.log('[🔌 Status] Reconnect blocked - in cooldown period after disconnect');
      return;
    } else if (reconnectCooldown && isWebRTCUpdate) {
      console.log('[🔌 Status] WebRTC update allowed despite cooldown (independent system)');
    }

    // Reconnect detection: Push latest message to Node-RED
    const isReconnect = wasDisconnected && !window.isInitialLoad;
    if (isReconnect && window.pushLatestMessageToNodeRed) {
      console.log('[🔌 Status] Reconnect detected, pushing to Node-RED');
      window.pushLatestMessageToNodeRed();
    }

    wasDisconnected = false;

    // Body classes for CSS
    document.body.classList.add('is-connected');
    document.body.classList.remove('is-disconnected');

    // Update skipped status (if chat.js is loaded)
    if (window.updateSkippedStatus) {
      window.updateSkippedStatus();
    }

    // Clear timeouts
    if (connectionTimeout) clearTimeout(connectionTimeout);
    if (disconnectTimeout) clearTimeout(disconnectTimeout);

  } else {
    // Mark as disconnected for reconnect detection
    wasDisconnected = true;

    // Start cooldown to prevent false reconnects during TD shutdown
    reconnectCooldown = true;
    console.log('[🔌 Status] Cooldown started - blocking reconnects for 6 seconds');

    // Reset all TD signals immediately (prevents false "online" during cooldown)
    console.log('[🔌 Status] Resetting all TD signals (clock, trigger, playing)');
    signals.clock = false;
    signals.trigger = false;
    signals.playing = false;

    // Clear signal timeouts
    if (clockTimeout) clearTimeout(clockTimeout);
    if (window.triggerTimeout) clearTimeout(window.triggerTimeout);
    if (window.playingTimeout) clearTimeout(window.playingTimeout);

    // Update UI to reflect signal changes
    console.log('[🔌 Status] Updating status after signal reset');
    updateOverallStatus();

    // End cooldown after 6 seconds
    setTimeout(() => {
      reconnectCooldown = false;
      console.log('[🔌 Status] Cooldown ended - reconnects allowed again');
    }, RECONNECT_COOLDOWN_MS);

    // Body classes for CSS
    document.body.classList.add('is-disconnected');
    document.body.classList.remove('is-connected');

    // Clear existing disconnect timeout
    if (disconnectTimeout) clearTimeout(disconnectTimeout);

    // Delay showing disconnected (5s, or instant for WebRTC)
    const delay = instantOffline ? 0 : DISCONNECT_DELAY_MS;
    disconnectTimeout = setTimeout(() => {
      connectionStatus.textContent = "";
      connectionStatus.classList.remove("connected", "hidden");
      connectionStatus.classList.add("disconnected");
      if (!instantOffline) {
        console.log('[🔌 Status] Disconnect delay (5s) finished');
      }

      // Update skipped status (if chat.js is loaded)
      if (window.updateSkippedStatus) {
        window.updateSkippedStatus();
      }
    }, delay);
  }
};

/* ------------------------------------------------------------
   Package Loading & Initialization
------------------------------------------------------------ */

/**
 * Load Packages Sequentially
 *
 * Order matters:
 * 1. Status button (UI foundation)
 * 2. Backend status (initial online/offline state)
 * 3. Socket connection (signal listeners)
 * 4. Chat package (if mode includes 'chat')
 * 5. Visual Blocks package (if mode includes 'visual-blocks')
 * 6. WebRTC package (if mode includes 'webrtc')
 */
async function loadLayers() {
  console.log('  → Creating status button...');
  createStatusButton();

  // ========== Overlay laden (nur WebRTC-Modes) ==========
  if (mode.includes('webrtc')) {
    console.log('  → Loading Overlay...');
    await import('../../overlay/src/overlay.css');

    const { initOverlay, updateOverlayStatus, closeOverlay, isOverlayActive } =
      await import('../../overlay/src/overlay.js');

    const textsModule = await import('../../overlay/overlay-texts.json', {
      assert: { type: 'json' }
    });

    await initOverlay(mode, textsModule.default);
    window.updateOverlayStatus = updateOverlayStatus;
    window.closeOverlay = closeOverlay;
    window.isOverlayActive = isOverlayActive;

    console.log('  ✓ Overlay loaded');
  }
  // ========================================================

  console.log('  → Fetching initial status from backend...');
  const backendStatus = await initStatusFromBackend();

  // ========== Overlay mit Backend-Status updaten ==========
  if (mode.includes('webrtc') && window.updateOverlayStatus) {
    window.updateOverlayStatus(backendStatus);
    console.log('  → Overlay status:', backendStatus ? 'ONLINE' : 'OFFLINE');
  }
  // ========================================================

  console.log('  → Connecting to Status Socket (Multi-Signal)...');
  connectStatusSocket();

  if (mode.includes('chat')) {
    console.log('  → Loading Chat...');
    await import('../../chat/src/chat.css');
    await import('../../chat/src/chat.js');
    console.log('  ✓ Chat loaded');
  }

  if (mode.includes('visual-blocks')) {
    console.log('  → Loading Visual Blocks...');
    await import('../../visual-blocks/src/visual.css');
    await import('../../visual-blocks/src/visual.js');
    console.log('  ✓ Visual Blocks loaded');
  }

  if (mode.includes('webrtc')) {
    console.log('  → Loading WebRTC Video + Audio...');
    await import('../../webrtc-video-audio/src/webrtc.css');
    await import('../../webrtc-video-audio/src/webrtc.js');
    console.log('  ✓ WebRTC Video + Audio loaded');
  }

  console.log('✅ Performance: Load complete');
}

/* Reveal the static markup that index.html's inline critical CSS keeps hidden
   until now (chat input fields, the "[synchronized]" label, the reel video).
   Every mode-specific stylesheet has resolved at this point, so there is
   nothing left to flash unstyled.

   In `finally`, deliberately: if any import above fails, the page must still
   become visible - otherwise a single failed request would leave it blank
   forever. A partially styled page is recoverable, an invisible one is not. */
loadLayers().finally(() => {
  document.documentElement.setAttribute('data-styles-ready', '');
});

/* ------------------------------------------------------------
   Circle-Only Update Function
   Used when button is managed separately (e.g., WebRTC play/pause)
------------------------------------------------------------ */

/**
 * Update ONLY the Status Circle
 *
 * Use when button state is managed elsewhere (e.g., webrtc.js controls button)
 * This ensures circle shows correct online/offline state independently
 *
 * @param {boolean} isOnline - Whether system is online (green circle) or offline (gray circle)
 */
function updateCircleOnly(isOnline) {
  const connectionStatus = document.getElementById('connection-status');
  if (!connectionStatus) return;

  console.log(`[🔌 Status] Updating circle only: ${isOnline ? 'GREEN' : 'GRAY'}`);

  connectionStatus.classList.remove('hidden');

  if (isOnline) {
    connectionStatus.classList.remove('disconnected');
    connectionStatus.classList.add('connected');
  } else {
    connectionStatus.classList.remove('connected');
    connectionStatus.classList.add('disconnected');
  }
}

window.updateCircleOnly = updateCircleOnly;
